<template>
    <div class="marketing-site-templates templates-container">
        <div v-if="loading" class="templates-spinner-container">
            <ds-spinner />
        </div>
        <div v-else class="templates">
            <start-from-scratch-card @click-create="$emit('click-create')" />
            <template v-if="templates.length">
                <template-group-card
                    v-for="(template, index) in templates"
                    :key="template.id"
                    :template-group="template"
                    :is-previewing="false"
                    :show-select="true"
                    @select="handleTemplateSelected(template.id, template.title)"
                    @preview="handleTemplatePreview(template.htmlPreviewUrl)"
                >
                    <template v-if="isMyTemplatesTab" #menu>
                        <template-card-menu
                            :data-qa="`template-card-menu-${index}`"
                            @rename="handleRenameTemplate(template)"
                            @copy="handleCopyTemplate(template)"
                            @delete="handleDeleteTemplateModalOpen(template, index)"
                            @edit="handleEditTemplate(template)"
                        />
                    </template>
                </template-group-card>
            </template>
        </div>
        <ds-modal
            class="delete-template-modal"
            size="xs"
            :title="confirmDeleteTitle"
            :is-open="!!pendingDeleteTemplateGroup"
            @close="closeDeleteTemplateGroupModal"
        >
            <div class="modal-body">
                <div class="confirmation-text">
                    {{ confirmDeleteText }}
                </div>
                <div class="confirm-delete-actions">
                    <ds-text-button @click="closeDeleteTemplateGroupModal">
                        <div>{{ cancelDeleteButtonLabel }}</div>
                    </ds-text-button>
                    <ds-text-button @click="confirmDeleteTemplateGroup">
                        <div>{{ confirmDeleteButtonLabel }}</div>
                    </ds-text-button>
                </div>
            </div>
        </ds-modal>

        <footer v-if="templates.length && showPagination" class="pagination-footer">
            <pagination-control
                :total-count="totalSize"
                :limit="limit"
                :offset="offset"
                @page="updatePage"
            />

            <div class="page-size">
                <ds-select-field
                    v-model="limit"
                    class="limit-select"
                    dense
                    bind-value-only
                    :options="pageSizes"
                />
                Per Page
            </div>
        </footer>
    </div>
</template>

<script>

import PaginationControl from '../shared/PaginationControl';
import StartFromScratchCard from './StartFromScratchCard';
import TemplateCardMenu from './TemplateCardMenu';
import TemplateGroupCard from './TemplateGroupCard';
import { deleteTemplateGroup } from '../util/marketing/marketingSiteEditorApi';

export const DEFAULT_LIMIT = 25;


export default {
    components: {
        PaginationControl,
        StartFromScratchCard,
        TemplateCardMenu,
        TemplateGroupCard,
    },
    props: {
        renameTemplate: {
            type: Function,
        },
        copyTemplate: {
            type: Function,
        },
        showPagination: {
            type: Boolean,
            default: false,
        },
        getTemplates: {
            type: Function,
            required: true,
        },
        source: {
            type: String,
            required: true,
        },
        isMyTemplatesTab: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: true,
            pendingDeleteTemplateGroup: null,
            templates: [],
            pageToken: '0',
            totalPages: 0,
            totalSize: 0,
            limit: Number(this.pageSize) > DEFAULT_LIMIT ? Number(this.pageSize) : DEFAULT_LIMIT,
            pageSizes: [
                { label: '10', value: 10 },
                { label: '25', value: 25 },
            ],
        };
    },

    mounted () {
        this.load();
    },

    watch: {
        limit() {
            this.reload();
        },
    },

    computed: {
        confirmDeleteTitle() {
            return 'Delete Landing Page';
        },
        confirmDeleteText() {
            return 'Are you sure you want to delete this landing page?';
        },
        confirmDeleteButtonLabel() {
            return 'Ok';
        },
        cancelDeleteButtonLabel() {
            return 'Cancel';
        },

        offset() {
            let response = (this.pageToken * this.limit) - this.limit;

            if (this.pageToken < 1) {
                response = (this.totalPages * this.limit) - this.limit;
            }

            return response;
        },
    },

    methods: {
        closeDeleteTemplateGroupModal() {
            this.pendingDeleteTemplateGroup = null;
        },

        async confirmDeleteTemplateGroup() {
            await deleteTemplateGroup(this.pendingDeleteTemplateGroup.id);
            this.templates = this.templates.filter((template) => template.id !== this.pendingDeleteTemplateGroup.id);
            this.closeDeleteTemplateGroupModal();
        },

        handleDeleteTemplateModalOpen(template) {
            this.pendingDeleteTemplateGroup = template;
        },
        async handleRenameTemplate(templateGroup) {
            const res = await this.renameTemplate(templateGroup);

            if (res) this.load();
        },
        async handleCopyTemplate(templateGroup) {
            const res = await this.copyTemplate(templateGroup, this.source);

            if (res) this.load();
        },
        handleEditTemplate(template) {
            this.$emit('edit', template);
        },
        reload() {
            this.pageToken = 0;
            this.load();
        },

        updatePage({ offset }) {
            this.pageToken = ((offset + this.limit) / this.limit) - 1;
            this.load();
        },

        async load() {
            this.loading = true;

            const response = await this.getTemplates(this.pageToken, this.limit);

            if (response?.data?.templateGroups) {
                const { data } = response;

                this.templates = data.templateGroups;
                this.pageToken = Number(data?.nextPage);
                this.totalPages = Number(data?.pages);
                this.totalSize = Number(data?.totalSize);
            }
            this.loading = false;
        },
        handleTemplateSelected(sourceTemplateId, templateTitle) {
            this.$emit('select-template', { sourceTemplateId, templateTitle, source: this.source });
        },
        handleTemplatePreview(url) {
            this.$emit('preview-template', { url, source: this.source });
        },
    },
};
</script>

<style  lang="scss" scoped>
$card-size: px-to-rem(300);
div.marketing-site-templates {
    min-height: 100%;
    justify-content: space-between;
}
.templates-container {
    flex: 1;
    padding: 2rem;
    @include overflowY;
}
.templates {
    display: grid;
    grid-template-columns: repeat(auto-fill, $card-size);
    grid-gap: 1.5rem;
    justify-content: center;

    &> * {
        width: $card-size;
        height: $card-size;
    }
}
.templates-spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
.confirm-delete-actions {
    display: flex;
    flex-direction: row;
    margin: 1.5rem 0 0 auto;
}
.confirm-delete-actions button {
    margin-left: 1.5rem;
}
.confirmation-text {
    color: rgba(0,0,0,.6);
    font-size: 1.5rem;
    line-height: 1.5rem;
    margin: 0;
}
footer {
    margin-top: 1rem;
    padding-top: $spacing-100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: inset 0 1px 0 $color-gray-200;

    @media($small) {
        grid-template-columns: 100%;
        justify-content: center;
        text-align: center;
    }

    .page-size {
        display: grid;
        grid-template-columns: px-to-rem(80) auto;
        align-items: center;
        grid-gap: $spacing-100;
        @media($small) { display: none }
    }
}
</style>
